import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../../shared/models/user.model';
import { AuthFlowService } from './auth-flow.service';

export interface LoginCredentials {
  email: string;
  password: string;
  remember?: number;
  turnstileToken?: string;
}

export interface RegisterCredentials {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  turnstileToken: string;
}

export interface FacebookLoginResponse {
  accessToken: string;
  userID: string;
  name?: string;
  email?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  
  // Replace with your actual API endpoint
  private baseUrl = `${environment.baseUrl}/api`;
  private _authFlowService?: AuthFlowService;

  constructor(
    private http: HttpClient,
    private injector: Injector
  ) {
    // Check if user is already logged in
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      this.currentUserSubject.next(JSON.parse(storedUser));
    }
  }

  // Lazy getter to avoid circular dependency
  private get authFlowService(): AuthFlowService {
    if (!this._authFlowService) {
      this._authFlowService = this.injector.get(AuthFlowService);
    }
    return this._authFlowService;
  }

  initializeCSRF(): Observable<void> {
    return this.http.get<void>(`${environment.baseUrl}/sanctum/csrf-cookie`);
  }

  loginWithFacebook(): Observable<User> {
    // In a real application, you would integrate with the Facebook SDK
    // and handle the OAuth flow. For now, we'll simulate a successful login
    return this.http.get(`${this.baseUrl}/facebook/connect`)
      .pipe(
        tap((res: any) => {
          console.log(res);
        })
      );
  }

  login(credentials: LoginCredentials): Observable<User> {
    return this.http.post<User>(`${this.baseUrl}/login`, credentials)
      .pipe(
        tap((response: any) => {
          // Cache user data immediately after login for guards to use
          const user = response?.data || response;
          localStorage.setItem('user', JSON.stringify(user));
          this.currentUserSubject.next(user);
        })
      );
  }

  loginOnLocalDevelopment(): Observable<User> {
    return this.http.get(`${this.baseUrl}/development/login`)
      .pipe(
        tap((user: any) => {
          localStorage.setItem('user', JSON.stringify(user.data));
          window.localStorage.setItem('token', user.token);
          this.currentUserSubject.next(user.data);
        })
      );
  }

  register(credentials: RegisterCredentials): Observable<User> {
    // Transform credentials to match API format
    const payload = {
      first_name: credentials.firstName,
      last_name: credentials.lastName,
      email: credentials.email,
      password: credentials.password,
      password_confirmation: credentials.confirmPassword,
      'cf-turnstile-response': credentials.turnstileToken
    };

    return this.http.post<User>(`${this.baseUrl}/register`, payload)
      .pipe(
        tap(user => {
          localStorage.setItem('user', JSON.stringify(user));
          this.currentUserSubject.next(user);
        })
      );
  }

  isAuthenticated(): boolean {
    return window.localStorage.getItem('user') ? true : false;
  }

  getToken(): string | null {
    return this.currentUserSubject.value?.token || null;
  }

  processFacebookToken(token: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/auth/facebook/callback`, { token })
      .pipe(
        tap((response: any) => {
          if (response.token) {
            localStorage.setItem('token', response.token);
            this.currentUserSubject.next(response.user);
          }
        })
      );
  }

  getUserProfile() {
    return this.http.get(`${this.baseUrl}/me` )
      .pipe(
        tap((response: any) => {
          localStorage.setItem('user', JSON.stringify(response.data));
          this.currentUserSubject.next(response.data);
          // this.authFlowService.redirectToAppropriateRoute(response.data);
        })
      );
  }

  logout() {
    return this.http.post(`${this.baseUrl}/logout`, {})
      .pipe(
        tap(() => {
          localStorage.removeItem('user');
          this.currentUserSubject.next(null);
        })
      );
  }

  forgotPassword(email: string, turnstileToken: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/forget-password`, { 
      email,
      'cf-turnstile-response': turnstileToken
    });
  }

  resetPassword(token: string, email: string, password: string, passwordConfirmation: string): Observable<any> {
    return this.http.post(`${this.baseUrl}/reset-password?token=${token}&email=${encodeURIComponent(email)}`, {
      password,
      password_confirmation: passwordConfirmation
    });
  }

  resendVerificationEmail(): Observable<any> {
    return this.http.post(`${this.baseUrl}/resend-verification-email`, {});
  }

  verifyEmail(id: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/email/verify/${id}`);
  }
} 